import { capitalize } from "../../helpers/string-util.js";
import AbstractStream from "./AbstractStream.js";

/**
 * Stream using oData queries to fetch items.
 *
 * @see {@link https://docs.microsoft.com/en-us/dynamics365/fin-ops-core/dev-itpro/data-entities/odata}
 * @param {function(spec): Promise} fetcher - This function is
 *   expected to return a promise resolving to an object with a
 *   `value` property containing an array of entities. The object may
 *   also contain an `@odata.Count` property containing the total
 *   number of entities that the server knows about. The function is
 *   passed a single argument object containing a `filter` property:
 *   the value of this property is a collection of query parameters
 */
export default class ODataStream extends AbstractStream {
	constructor({ fetcher }) {
		super(...arguments);

		this._fetcher = fetcher;
	}

	async fetch({
		searchingOptions = {},
		sortingOptions = {},
		groupingOptions = {},
		paginatingOptions = {},
	} = {}) {
		const filter = this._makeODataFilter({
			searchingOptions,
			sortingOptions,
			groupingOptions,
			paginatingOptions,
		});

		const result = await this._fetcher({ filter });

		if (filter["$top"]) {
			return {
				items: result.value,
				unpaginatedSize: result["@odata.Count"],
			};
		}

		return {
			items: result,
			unpaginatedSize: result.length,
		};
	}

	_makeODataFilter({
		searchingOptions,
		sortingOptions,
		groupingOptions,
		paginatingOptions,
	}) {
		const filter = {};

		if (searchingOptions.searchString) {
			filter["term"] = searchingOptions.searchString;
		}

		if (sortingOptions.propertyName) {
			const name = capitalize(sortingOptions.propertyName);

			const sortOrder = sortingOptions.isAscendingOrder ? "asc" : "desc";

			filter["$orderby"] = `${name} ${sortOrder}`;
		}

		if (groupingOptions.propertyName) {
			const name = groupingOptions.propertyName;
			filter["$apply"] = `groupby((${name}))`;
		}

		if (paginatingOptions.numberOfItemsToFetch) {
			filter["$skip"] = paginatingOptions.numberOfItemsToSkip || 0;
			filter["$top"] = paginatingOptions.numberOfItemsToFetch;
			filter["$count"] = true;
		}

		return filter;
	}
}
